// WS-8 P2 — Export Excel del Cruce / Conciliación (client-side, sin endpoint
// nuevo — patrón factQExport). Exporta las filas VISIBLES (con los filtros de
// cliente/área/estado aplicados) espejando las columnas de la tabla, más la
// identidad del grupo (cliente/planta/área) y el detalle del cierre manual.

import * as XLSX from 'xlsx';
import { semanaOperativaKeyDe, type CruceEstado, type CruceFila } from 'schemas';

const DOW = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];

const ESTADO: Record<CruceEstado, string> = {
  terminada: 'Terminada',
  en_proceso: 'En proceso',
  sin_programar: 'Sin programar',
};

function diaDe(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  return DOW[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!;
}

/** Número de semana operativa de una fecha YYYY-MM-DD (mismo criterio que la tabla). */
function semanaDe(iso: string | null, diaInicioSemana: number): number | '' {
  if (!iso) return '';
  return Number(semanaOperativaKeyDe(iso.slice(0, 10), diaInicioSemana).slice(-2));
}

const HEADER = [
  'Semana',
  'Día',
  'Fecha prog. desde',
  'Fecha prog. hasta',
  'Cliente',
  'Planta',
  'Área',
  'Orden',
  'SKU',
  'Descripción',
  'Total programado',
  'Prod. acumulada',
  'Validada supervisor',
  'Diferencia',
  'Cumplimiento %',
  'Cumplimiento real %',
  'No entregadas cliente',
  'Estado',
  'Cierre manual',
  'Motivo cierre',
  'Paros del cierre',
  'Observación programación',
  'Observación cierre',
];

const anchos = [
  8, 11, 15, 15, 14, 12, 20, 12, 10, 40, 15, 14, 17, 11, 14, 16, 18, 13, 12, 24, 40, 30, 30,
];

function fila(f: CruceFila, diaInicioSemana: number): (string | number)[] {
  return [
    semanaDe(f.fecha_programada_desde, diaInicioSemana),
    diaDe(f.fecha_programada_desde),
    f.fecha_programada_desde ?? '',
    f.fecha_programada_hasta ?? '',
    f.cliente_nombre,
    f.planta_nombre,
    `${f.area_codigo} · ${f.area_nombre}`,
    f.numero_op ?? 'sin OP',
    f.sku_codigo_cliente,
    f.sku_descripcion,
    f.cantidad_programada ?? '',
    f.cantidad_capturada,
    f.cantidad_validada,
    f.diferencia ?? '',
    f.cumplimiento_pct != null ? Math.round(f.cumplimiento_pct) : '',
    f.cumplimiento_real_pct != null ? Math.round(f.cumplimiento_real_pct) : '',
    f.no_entregada_justificada,
    ESTADO[f.estado],
    f.cerrada_manual ? 'Sí' : 'No',
    f.motivo_conciliacion ?? '',
    (f.paros_conciliacion ?? [])
      .map((p) => `${p.fecha} · ${p.causal_madre} → ${p.causal} (${Math.round(p.minutos)}min)`)
      .join('; '),
    f.observacion ?? '',
    f.observacion_conciliacion ?? '',
  ];
}

/** Genera y descarga el Excel del cruce con las filas visibles. */
export function exportarCruce(
  filas: CruceFila[],
  rango: { desde: string; hasta: string },
  diaInicioSemana: number,
): void {
  const aoa: (string | number)[][] = [
    [`CRUCE / CONCILIACIÓN · ${rango.desde} → ${rango.hasta}`],
    HEADER,
    ...filas.map((f) => fila(f, diaInicioSemana)),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = anchos.map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Conciliación');
  XLSX.writeFile(wb, `conciliacion_${rango.desde}_${rango.hasta}.xlsx`);
}
